import * as Sentry from '@sentry/react';
import { AuthenticationError, TimeoutError } from '../api/apiErrors';
import { getKeycloakInstance, type KeycloakLike } from './keycloak';
import { REFRESH_TOKEN_MIN_VALIDITY } from './constants';

type RecoveryState = 'ready' | 'unavailable' | 'expired';
let state: RecoveryState = 'ready';
const listeners = new Set<() => void>();
const recoveryListeners = new Set<() => void>();
let generation = 0;
let activeRefresh:
  | { instance: KeycloakLike; promise: Promise<boolean>; forced: boolean }
  | undefined;
// When Keycloak last accepted the refresh token, proving the session is live.
let lastConfirmedAt = 0;
const CONFIRMATION_WINDOW_MS = 30000;

const publish = (next: RecoveryState) => {
  if (state === next) return;
  const recovered = next === 'ready';
  state = next;
  for (const listener of listeners) listener();
  if (recovered) for (const listener of recoveryListeners) listener();
};
export const subscribeSessionRecovery = (listener: () => void) => {
  recoveryListeners.add(listener);
  return () => {
    recoveryListeners.delete(listener);
  };
};
export const subscribeTokenRecovery = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export const getTokenRecoveryState = () => state;
export const resetTokenRecovery = () => {
  generation += 1;
  activeRefresh = undefined;
  lastConfirmedAt = 0;
  // Session replacement is handled by the identity lifecycle, not recovery.
  state = 'ready';
  for (const listener of listeners) listener();
};

const tokenIsValid = (instance: KeycloakLike) => {
  const expires = instance.tokenParsed?.exp;
  return (
    !!instance.token &&
    expires !== undefined &&
    expires - Date.now() / 1000 + (instance.timeSkew ?? 0) > 0
  );
};

async function failureDetails(error: unknown) {
  const response =
    typeof error === 'object' && error !== null && 'response' in error
      ? error.response
      : undefined;
  const status = response instanceof Response ? response.status : undefined;
  let oauthError: string | undefined;
  if (response instanceof Response) {
    try {
      const body: unknown = await response.clone().json();
      const code =
        typeof body === 'object' && body !== null && 'error' in body
          ? body.error
          : undefined;
      if (
        typeof code === 'string' &&
        [
          'invalid_grant',
          'invalid_client',
          'unauthorized_client',
          'temporarily_unavailable',
          'server_error',
        ].includes(code)
      ) {
        oauthError = code;
      }
    } catch {
      /* A response body is optional. */
    }
  }
  const name = error instanceof Error ? error.name : undefined;
  const temporary =
    status === undefined
      ? name === 'TypeError' ||
        name === 'NetworkError' ||
        name === 'TimeoutError' ||
        name === 'AbortError'
      : status === 408 || status === 429 || status >= 500;
  return { status, oauthError, temporary };
}

async function updateWithDeadline(instance: KeycloakLike, minValidity: number) {
  let timeout: number | undefined;
  try {
    return await Promise.race([
      instance.updateToken(minValidity),
      new Promise<never>((_, reject) => {
        timeout = window.setTimeout(
          () => reject(new TimeoutError('Token renewal timed out')),
          10000,
        );
      }),
    ]);
  } finally {
    window.clearTimeout(timeout);
  }
}

/** Shared by polling, foreground recovery, and authenticated requests. */
export function refreshSessionToken(
  minValidity = REFRESH_TOKEN_MIN_VALIDITY,
): Promise<boolean> {
  const instance = getKeycloakInstance();
  const forced = minValidity === -1;
  if (activeRefresh?.instance === instance) {
    if (!forced || activeRefresh.forced) return activeRefresh.promise;
    // A conditional refresh may succeed without contacting Keycloak, which
    // would not replace a token the server has already rejected.
    return activeRefresh.promise.then((refreshed) => {
      if (refreshed) return true;
      if (instance !== getKeycloakInstance())
        throw new AuthenticationError('Session changed');
      return refreshSessionToken(-1);
    });
  }
  if (state === 'expired')
    return Promise.reject(new AuthenticationError('Session expired'));
  if (!instance.authenticated) return Promise.resolve(false);
  const startedGeneration = generation;
  const current = () =>
    startedGeneration === generation && instance === getKeycloakInstance();
  const promise = (async () => {
    for (let attempt = 0; ; attempt += 1) {
      if (!current()) throw new AuthenticationError('Session changed');
      try {
        const refreshed = await updateWithDeadline(instance, minValidity);
        if (!current()) throw new AuthenticationError('Session changed');
        if (refreshed) lastConfirmedAt = Date.now();
        publish('ready');
        return refreshed;
      } catch (error) {
        if (!current()) throw new AuthenticationError('Session changed');
        const details = await failureDetails(error);
        if (!current()) throw new AuthenticationError('Session changed');
        if (details.temporary && attempt < 3) {
          await new Promise<void>((resolve) =>
            window.setTimeout(resolve, 1000 * 2 ** attempt),
          );
          continue;
        }
        // The SDK exception's response may contain credentials; report safe fields only.
        Sentry.captureException(new Error('Token renewal failed'), {
          level: details.temporary ? 'error' : 'warning',
          tags: {
            auth: 'tokenRefresh',
            reason: details.temporary ? 'temporary' : 'rejected',
          },
          extra: {
            httpStatus: details.status,
            oauthError: details.oauthError,
            attempts: attempt + 1,
            documentVisible: document.visibilityState === 'visible',
          },
        });
        publish(details.temporary ? 'unavailable' : 'expired');
        if (details.temporary && minValidity !== -1 && tokenIsValid(instance))
          return false;
        throw new AuthenticationError(
          details.temporary
            ? 'Token renewal temporarily unavailable'
            : 'Session expired',
        );
      }
    }
  })();
  const tracked = promise.finally(() => {
    if (activeRefresh?.promise === tracked) activeRefresh = undefined;
  });
  activeRefresh = { instance, promise: tracked, forced };
  return tracked;
}

/**
 * Request path: a still-valid token is used immediately while any renewal
 * (including its retries) continues in the background. This deliberately
 * includes an expired session whose access token has not yet lapsed: the
 * server still accepts it, and a 401 then fails through the forced refresh.
 */
export function ensureSessionToken(minValidity: number): Promise<boolean> {
  const refresh = refreshSessionToken(minValidity);
  if (minValidity === -1 || !tokenIsValid(getKeycloakInstance())) {
    return refresh;
  }
  void refresh.catch(() => undefined);
  return Promise.resolve(false);
}

/**
 * Called when the server rejects a request after renewal. A 401 alone may be
 * an authorization problem, so only a refresh Keycloak rejects marks the
 * session expired.
 */
export async function verifySession() {
  if (Date.now() - lastConfirmedAt < CONFIRMATION_WINDOW_MS) return;
  await refreshSessionToken(-1).catch(() => undefined);
}
